import React, { useEffect, useRef } from "react";
import { Typography } from "@mui/material";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import { FormProvider } from "@/src/provider/Form/FormProvider";
import { useLocation, useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { fetchCollection, queryKeys } from "@/src/services/reactQuery/reactQueryService";
import { Channel } from "@/src/types/Entities/Messaging/Channel";
import MessageFormStepInner from "./MessageFormStepInner";
import { MessageCreateData } from "@/src/types/Messaging/MessageCreateData";
import { TiptapEditorProvider } from "@/src/components/Tiptap/TiptapEditorProvider";
import { useCurrentEditor } from "@tiptap/react";
import MessageFormLoader from "@/src/components/Messaging/MessageForm/MessageFormLoader";
import ContentBox from "@/src/components/Box/ContentBox/ContentBox";

type NoteFormStepProps = {
  product: AbstractCase;
  setCurrentStep: Function;
  setMessageCreateData: Function;
  messageCreateData: MessageCreateData | null;
};

export default function MessageFormStep(props: NoteFormStepProps) {
  return (
    <TiptapEditorProvider>
      <MessageFormStepForm {...props} />
    </TiptapEditorProvider>
  );
}

function MessageFormStepForm({ setCurrentStep, product, setMessageCreateData, messageCreateData = null }: NoteFormStepProps) {
  const { editor } = useCurrentEditor();
  const [searchParams, setSearchParams] = useSearchParams();
  const startWithDocumentUpload = searchParams.get("documentUpload") === "true";

  const location = useLocation();
  const initializationData = location.state;
  const isInitialized = useRef(false);

  const channelFilter = {
    case: product.id,
  };

  const { data: caseChannels, isLoading: isLoadingChannels } = useQuery({
    queryKey: queryKeys.collection("/channels", channelFilter),

    queryFn: () => fetchCollection("/channels", channelFilter),
  });
  const isExistingDocumentSubmission =
    messageCreateData !== null && typeof messageCreateData.conversation !== "string" && messageCreateData.conversation.type === "submitDocuments";

  useEffect(() => {
    if (isInitialized.current || (!initializationData && !startWithDocumentUpload) || !caseChannels) {
      return;
    }
    isInitialized.current = true;

    if (startWithDocumentUpload) {
      onSubmit({
        values: {
          type: "submitDocuments",
        },
      });
      setSearchParams((currentSearchParams) => {
        const nextSearchParams = new URLSearchParams(currentSearchParams);
        nextSearchParams.delete("documentUpload");
        return nextSearchParams;
      }, { replace: true });
      return;
    }

    editor?.commands.setContent(initializationData.editor);

    onSubmit({ overwriteMessageText: editor?.getHTML() });
    location.state = null;
  }, [initializationData, caseChannels, startWithDocumentUpload]);

  useEffect(() => {
    if (messageCreateData?.messageText && !isExistingDocumentSubmission) {
      editor?.commands.setContent(messageCreateData.messageText);
    }
  }, []);

  const conversationId = searchParams.get("conversationId");
  const isConversationAnswer = !!conversationId;

  const onSubmit = ({ values, overwriteMessageText }: Record<string, any>) => {
    const isDocumentSubmission = values?.type === "submitDocuments";
    const messageText = isDocumentSubmission
      ? "<p>Diese Nachricht dient nur der Übermittlung der angehängten Unterlagen.</p>"
      : overwriteMessageText || editor?.getHTML();
    const mainChannel = caseChannels.find((channel: Channel) => channel.type === "all");

    if (!isConversationAnswer && !mainChannel) {
      return;
    }

    const conversationPayload = isConversationAnswer
      ? "/conversations/" + conversationId
      : {
          subject: isDocumentSubmission ? "Nur Unterlagen (kein Nachrichteninhalt)" : values.subject,
          type: values.type,
          channel: "/channels/" + mainChannel.id,
        };

    const createMessageData = {
      messageText,
      conversation: conversationPayload,
    };

    setMessageCreateData(createMessageData);
    setCurrentStep((currentStep: number) => {
      return currentStep + 1;
    });
  };

  let initialMessageType = product.isCustomerDataCompleted ? "" : "technicalQuestion";
  if (isExistingDocumentSubmission) {
    initialMessageType = "submitDocuments";
  }

  const initialValues = {
    type: initialMessageType,
    subject: "",
    message: "",
  };

  if (isLoadingChannels) {
    return <MessageFormLoader />;
  }

  if (!caseChannels) {
    return (
      <ContentBox>
        <Typography sx={{ margin: "1rem 0" }}>Es ist ein technischer Fehler aufgetreten, bitte melden Sie sich bei unserem Kundendienst.</Typography>
      </ContentBox>
    );
  }

  return (
    <FormProvider initialValues={initialValues} submitAction={onSubmit}>
      <MessageFormStepInner product={product} isConversationAnswer={isConversationAnswer} />
    </FormProvider>
  );
}
